import { notify } from '@affine/component';
import { I18n } from '@affine/i18n';
import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  type DatabaseRowSet,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine-block-database';
import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
} from '@blocksuite/affine-model';
import {
  databaseLineageColumns,
  readDatabaseCell,
  readDatabaseShardDirectoryDocIds,
  storedDatabaseCellValue,
} from '@streamlain/database-identity';
import {
  type LocalPublicationReceipt,
  PublicationError,
} from '@streamlain/doc-publication-contract';
import { MAX_GROUP_DOCS } from '@streamlain/doc-sync-taxonomy';
import {
  APP_SETTINGS_CONTAINER_TABLE,
  type AppBaseConfig,
  appSettingsChangeHasTarget,
  appSettingsChildTableKeys,
  type AppSettingsDatabaseBlockIds,
  appSettingsFromRows,
  appSettingsRowCells,
  appSettingsRowFromCells,
  appSettingsState,
  appSettingsToRows,
  configRowFieldCells,
  configRowFromCells,
  planAppSettingsChanges,
  rebaseAppSettings,
  SETTINGS_GENERATION_COLUMN,
  SETTINGS_REVISION_COLUMN,
  type SettingsGeneration,
  type SettingsRow,
  type SettingsRows,
  splitAppSettingsGenerations,
  type ViewConfigRow,
  ViewConfigRowInvalidError,
} from '@streamlain/view-config';
import { LiveData, Store } from '@toeverything/infra';
import {
  filter,
  firstValueFrom,
  fromEvent,
  map,
  Observable,
  scan,
  takeUntil,
} from 'rxjs';

import type { DocService } from '../../doc';
import type { PublicationSettlementPort } from '../../doc/services/publication-settlement';
import {
  type AppSettingsStore,
  type AppSettingsWorkspace,
  type AppSettingsYDoc,
  openDatabaseRootDoc,
  openWorkspaceDoc,
} from './app-settings-databases';

/** Where the config database and the Config tables of an App Container are stored. */
export interface AppSettingsLocation {
  readonly configDbBlockId: string;
  readonly settingsDatabaseBlockIds: AppSettingsDatabaseBlockIds;
}

/** The database of every table of a location, container first. */
export function appSettingsLocationTables(
  location: AppSettingsLocation
): ReadonlyArray<{ readonly tableKey: string; readonly blockId: string }> {
  return [
    {
      tableKey: APP_SETTINGS_CONTAINER_TABLE,
      blockId: location.configDbBlockId,
    },
    ...appSettingsChildTableKeys().map(tableKey => {
      const blockId = location.settingsDatabaseBlockIds[tableKey];
      if (!blockId) throw new Error(`App has no Config table ${tableKey}`);
      return { tableKey, blockId };
    }),
  ];
}

/**
 * Attempts before a change is given up: per write against newer local
 * settings, and per change against server refusals.
 */
export const APP_SETTINGS_WRITE_ATTEMPTS = 3;

export type AppSettingsWriteErrorCode =
  | 'APP_SETTINGS_GROUP_TOO_LARGE'
  | 'APP_SETTINGS_WRITE_CONFLICT'
  | 'APP_SETTINGS_RESTORED'
  | 'APP_SETTINGS_REFUSED';

const WRITE_ERROR_MESSAGES = {
  APP_SETTINGS_GROUP_TOO_LARGE: 'com.streamlain.appSettings.write.tooLarge',
  APP_SETTINGS_WRITE_CONFLICT: 'com.streamlain.appSettings.write.conflict',
  APP_SETTINGS_RESTORED: 'com.streamlain.appSettings.write.restored',
  APP_SETTINGS_REFUSED: 'com.streamlain.appSettings.write.refused',
} as const satisfies Record<AppSettingsWriteErrorCode, string>;

/** The name of a Config table in the UI language. */
export function appSettingsTableLabel(tableKey: string): string {
  return I18n.t(`com.streamlain.appSettings.table.${tableKey}`);
}

/** A settings change the store could not write; no table changed. */
export class AppSettingsWriteError extends Error {
  override name = 'AppSettingsWriteError';
  constructor(
    readonly code: AppSettingsWriteErrorCode,
    /** The part of the settings the change was about, as the user named it. */
    readonly area: string,
    override readonly cause?: unknown
  ) {
    super(`${code}: ${area}`);
  }

  /** The message the user reads, in the UI language. */
  userMessage(): string {
    return I18n.t(WRITE_ERROR_MESSAGES[this.code], { area: this.area });
  }
}

function sameCell(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** A stored configuration row that fails the row schema. */
export interface InvalidViewConfigRow {
  viewId: string;
  error: ViewConfigRowInvalidError;
}

/**
 * Whether the rows of the container are read: `loading` while a Config
 * table, a row shard or a directory part is on its way before the first
 * read (afterwards the last read rows stay until the newer ones are
 * complete), `unavailable` once the settings cannot be read (an invalid
 * row, or a directory part the server confirmed it does not hold). Only
 * `ready` rows are the rows of the container.
 */
export type ViewConfigRowsState = 'loading' | 'ready' | 'unavailable';

interface ViewConfigRowsSnapshot {
  state: ViewConfigRowsState;
  rows: ViewConfigRow[];
  invalidRows: InvalidViewConfigRow[];
  /** Rows of the valid generation per Config table; empty unless `ready`. */
  tableRows: SettingsRows;
}

/** The Config tables as their rows read: state and the rows of the valid generation. */
export interface ViewConfigTablesSnapshot {
  readonly state: ViewConfigRowsState;
  /** Empty unless `state` is `ready`. */
  readonly rows: SettingsRows;
}

const NO_TABLE_ROWS: SettingsRows = {};

const LOADING_SNAPSHOT: ViewConfigRowsSnapshot = {
  state: 'loading',
  rows: [],
  invalidRows: [],
  tableRows: NO_TABLE_ROWS,
};

type CompleteShards = Extract<
  NonNullable<ReturnType<DatabaseBlockDataSource['getCompleteShardDirectory']>>,
  { status: 'ready' }
>['manifest']['shards'];

type TableRuntime = {
  dataSource: DatabaseBlockDataSource;
  hostModel: any;
  model: any;
};

/** A table's storage: on its way, read, or stored so that it cannot be read. */
type RuntimeRead =
  | { state: 'loading' }
  | { state: 'ready'; runtime: TableRuntime }
  | { state: 'unreadable'; error: Error };

/** The stored rows of one table, and the stored cells of each row. */
type TableRead = {
  rows: SettingsRow[];
  cells: Map<string, (columnId: string) => unknown>;
};

/** What a patch changes in the tables read. */
type PatchChanges = {
  changes: ReturnType<typeof planAppSettingsChanges>;
  /** Config row fields whose container cell changes. */
  containerCells: Record<string, unknown>;
};

/** One settings change as the caller made it, written until it holds or fails. */
type PatchWrite = {
  readonly viewId: string;
  readonly patch: Partial<ViewConfigRow>;
  /** The settings the change was made on. */
  readonly base: AppBaseConfig;
  /** The generation the change was made on; another one means a restore. */
  readonly generation: string;
  /** The Config tables the change is about, as the user reads them. */
  readonly area: string;
};

/** The settings of the container as its tables hold them right now. */
type SettingsRead = {
  viewId: string;
  state: SettingsGeneration;
  /** Rows of the valid generation. */
  rows: SettingsRows;
  containerCell: (columnId: string) => unknown;
};

function cellOfRow(
  row: { get: (key: string) => unknown },
  lineage: ReadonlyMap<string, ReturnType<typeof databaseLineageColumns>[0]>
): (columnId: string) => unknown {
  const stored = (cellKey: string) => {
    const value = row.get(cellKey) as { toJSON?: () => unknown } | undefined;
    return value && typeof value.toJSON === 'function' ? value.toJSON() : value;
  };
  return columnId => {
    const column = lineage.get(columnId);
    if (column)
      return readDatabaseCell(column, cellKey =>
        storedDatabaseCellValue(stored(cellKey))
      )?.value;
    return storedDatabaseCellValue(stored(columnId));
  };
}

/**
 * The config row of one App Container: the container row of its config
 * database (title, config row fields) and the settings its Config tables
 * hold, decoded and written with the same mapping as the server. A write is
 * one publication across every changed table plus the container row with
 * `:revision`+1. A write the server refuses is read again and applied on
 * the settings the server holds.
 */
export class ViewConfigRowStore extends Store {
  readonly rows$: LiveData<ViewConfigRow[]>;
  /**
   * Rows that exist in the config database but cannot be read. They are
   * never part of `rows$`; surfaces show them so an unreadable app does not
   * look like a loading one.
   */
  readonly invalidRows$: LiveData<InvalidViewConfigRow[]>;
  /** Whether `rows$` holds the rows of the container yet. */
  readonly state$: LiveData<ViewConfigRowsState>;
  /**
   * The rows of every Config table, from the same read as `rows$`: the
   * App Studio lists them, it never writes through them.
   */
  readonly tables$: LiveData<ViewConfigTablesSnapshot>;
  private readonly snapshot$: LiveData<ViewConfigRowsSnapshot>;
  private readonly tables: ReturnType<typeof appSettingsLocationTables>;
  private readonly runtimes = new Map<string, TableRuntime>();
  private writes: Promise<void> = Promise.resolve();
  /** Written changes still waiting for the server's answer. */
  private readonly settlements = new Set<Promise<void>>();
  private writeFailure: unknown = null;
  private readonly lifetime = new AbortController();

  constructor(
    private readonly docService: DocService,
    readonly location: AppSettingsLocation,
    private readonly settlement: PublicationSettlementPort
  ) {
    super();
    this.tables = appSettingsLocationTables(location);

    this.snapshot$ = LiveData.from<ViewConfigRowsSnapshot>(
      this.tableChanges().pipe(
        map(() => this.safeReadSnapshot()),
        // Rows read once stay the rows of the container while a newer row
        // shard or directory part is on its way (an installation lists its
        // rows before they arrive); readers never see the container empty.
        scan((shown, next) =>
          next.state === 'loading' && shown.state === 'ready' ? shown : next
        )
      ),
      LOADING_SNAPSHOT
    );
    this.rows$ = this.snapshot$.map(snapshot => snapshot.rows);
    this.invalidRows$ = this.snapshot$.map(snapshot => snapshot.invalidRows);
    this.state$ = this.snapshot$.map(snapshot => snapshot.state);
    this.tables$ = this.snapshot$.map(snapshot => ({
      state: snapshot.state,
      rows: snapshot.tableRows,
    }));
  }

  override dispose(): void {
    this.lifetime.abort();
    for (const runtime of this.runtimes.values()) runtime.dataSource.dispose();
    this.runtimes.clear();
    super.dispose();
  }

  /** Emits at once and whenever a table, one of its row shards or the set of open documents changes. */
  private tableChanges(): Observable<void> {
    return new Observable<void>(subscriber => {
      const tableWatchers = this.tables.map(({ blockId }) =>
        this.watchTable(blockId, () => emit())
      );
      const emit = () => {
        for (const watch of tableWatchers) watch.sync();
        subscriber.next();
      };
      const subscriptions = [
        this.getBlockSuiteDoc()?.slots?.blockUpdated?.subscribe(emit),
        // A document reopened after a refusal is a new instance.
        this.getWorkspace()?.slots?.docAdded?.subscribe(emit),
      ];
      emit();
      return () => {
        for (const watch of tableWatchers) watch.dispose();
        for (const subscription of subscriptions) subscription?.unsubscribe();
      };
    });
  }

  /** Resolves once every table is loaded again; rejects when the settings cannot be read. */
  private settingsLoaded(signal: AbortSignal): Promise<void> {
    return firstValueFrom(
      this.tableChanges().pipe(
        map(() => this.readSettings() !== null),
        filter(Boolean),
        map(() => undefined),
        takeUntil(fromEvent(signal, 'abort'))
      )
    );
  }

  getRow$(viewId: string): LiveData<ViewConfigRow | null> {
    return this.rows$.map(rows => rows.find(r => r.viewId === viewId) ?? null);
  }

  get defaultRow$(): LiveData<ViewConfigRow | null> {
    return this.rows$.map(rows => rows.find(r => r.isDefault) ?? null);
  }

  /**
   * Writes the patch as one publication. Throws at once when the row is not
   * there or the settings cannot be read. While a newer row shard is on its
   * way (the rows read before stay shown), the change waits for its rows. A
   * write that fails later is shown to the user and reported by
   * `flushLocalWrites`.
   */
  updateRow(viewId: string, patch: Partial<ViewConfigRow>): void {
    if (!this.readSettings()) {
      const area = this.tables
        .map(({ tableKey }) => appSettingsTableLabel(tableKey))
        .join(', ');
      this.enqueueWrite(async () => {
        await this.settingsLoaded(this.lifetime.signal);
        await this.writePatch(this.patchWrite(viewId, patch), 1);
      }, area);
      return;
    }
    const change = this.patchWrite(viewId, patch);
    this.enqueueWrite(() => this.writePatch(change, 1), change.area);
  }

  private patchWrite(
    viewId: string,
    patch: Partial<ViewConfigRow>
  ): PatchWrite {
    const read = this.requireRead(viewId, 'updateRow');
    // The settings the caller changed: a later attempt applies only that
    // change, on the generation it was made on or on a restored one.
    const base = appSettingsFromRows(read.viewId, read.rows);
    const { generation } = read.state;
    // The message names the Config tables the change is about.
    const area = this.changedTables(read, this.patchChanges(read, patch, base))
      .map(appSettingsTableLabel)
      .join(', ');
    return { viewId, patch, base, generation, area };
  }

  updateField<K extends keyof ViewConfigRow>(
    viewId: string,
    field: K,
    value: ViewConfigRow[K]
  ): void {
    this.updateRow(viewId, { [field]: value });
  }

  /** Waits for every queued write; rejects with the first failure since the last call. */
  async flushLocalWrites(): Promise<void> {
    await this.writes;
    const failure = this.writeFailure;
    this.writeFailure = null;
    if (failure) throw failure;
  }

  /**
   * Waits until the server answered every written change, including the
   * changes applied again after a refusal; rejects with the first failure
   * since the last flush. Offline it waits until the connection is back.
   */
  async flushServerWrites(): Promise<void> {
    for (;;) {
      await this.writes;
      if (!this.settlements.size) break;
      await Promise.all(this.settlements);
    }
    const failure = this.writeFailure;
    this.writeFailure = null;
    if (failure) throw failure;
  }

  private enqueueWrite(write: () => Promise<void>, area: string): void {
    this.writes = this.writes
      .then(write)
      .catch(error => this.reportWriteFailure(error, area));
  }

  private reportWriteFailure(error: unknown, area: string): void {
    // A disposed store ends its writes; nobody reads the message.
    if (this.lifetime.signal.aborted) return;
    this.writeFailure ??= error;
    console.error('[ViewConfigRowStore] settings write failed', error);
    notify.error({
      title:
        error instanceof AppSettingsWriteError
          ? error.userMessage()
          : I18n.t('com.streamlain.appSettings.write.failed', { area }),
    });
  }

  /**
   * Waits for the server's answer to a written change. A refusal for newer
   * server settings applies the change again on the settings pulled with
   * it, at most APP_SETTINGS_WRITE_ATTEMPTS times; other refusals fail it.
   */
  private settleWrite(
    receipt: LocalPublicationReceipt,
    rewrite: (round: number) => Promise<void>,
    area: string,
    round: number
  ): void {
    const { signal } = this.lifetime;
    const settlement = this.settlement
      .settle(receipt, signal)
      .then(async settled => {
        if (settled.state === 'done') return;
        if (settled.refusal === 'LIMIT_EXCEEDED')
          throw new AppSettingsWriteError('APP_SETTINGS_GROUP_TOO_LARGE', area);
        if (settled.refusal !== 'REMOTE_CONFLICT')
          throw new PublicationError(settled.refusal);
        if (round === APP_SETTINGS_WRITE_ATTEMPTS)
          throw new AppSettingsWriteError('APP_SETTINGS_REFUSED', area);
        await this.settingsLoaded(signal);
        this.enqueueWrite(() => rewrite(round + 1), area);
      })
      .catch(error => this.reportWriteFailure(error, area))
      .finally(() => this.settlements.delete(settlement));
    this.settlements.add(settlement);
  }

  /**
   * `round`: the how-manieth time the change is written after server
   * refusals. A change whose generation was restored meanwhile is written
   * only while the restored settings still hold what it changes.
   */
  private async writePatch(change: PatchWrite, round: number): Promise<void> {
    const { viewId, patch, base, generation, area } = change;
    for (let attempt = 1; ; attempt++) {
      if (!this.readSettings()) await this.settingsLoaded(this.lifetime.signal);
      const read = this.requireRead(viewId, 'updateRow');
      const restored = generation !== read.state.generation;
      if (
        restored &&
        patch.appBaseConfig &&
        !appSettingsChangeHasTarget(
          base,
          patch.appBaseConfig,
          appSettingsFromRows(read.viewId, read.rows)
        )
      )
        throw new AppSettingsWriteError('APP_SETTINGS_RESTORED', area);
      let plan: ReturnType<
        typeof DatabaseBlockDataSource.planRowSetGroup
      > | null;
      try {
        plan = this.planPatch(read, patch, base);
      } catch (error) {
        if (restored)
          throw new AppSettingsWriteError('APP_SETTINGS_RESTORED', area, error);
        throw error;
      }
      if (!plan) return;
      if (plan.documents > MAX_GROUP_DOCS)
        throw new AppSettingsWriteError('APP_SETTINGS_GROUP_TOO_LARGE', area);
      try {
        const receipt = await plan.commit();
        // An empty group writes nothing, so there is no answer to wait for.
        if (receipt)
          this.settleWrite(
            receipt,
            next => this.writePatch(change, next),
            area,
            round
          );
        return;
      } catch (error) {
        if (
          error instanceof PublicationError &&
          error.code === 'LIMIT_EXCEEDED'
        )
          throw new AppSettingsWriteError(
            'APP_SETTINGS_GROUP_TOO_LARGE',
            area,
            error
          );
        if (
          !(error instanceof PublicationError) ||
          error.code !== 'LOCAL_CONFLICT'
        )
          throw error;
        if (attempt === APP_SETTINGS_WRITE_ATTEMPTS)
          throw new AppSettingsWriteError(
            restored ? 'APP_SETTINGS_RESTORED' : 'APP_SETTINGS_WRITE_CONFLICT',
            area,
            error
          );
      }
    }
  }

  /** What the patch changes in the tables read; the settings change rebased on the stored ones. */
  private patchChanges(
    read: SettingsRead,
    patch: Partial<ViewConfigRow>,
    base: AppBaseConfig
  ): PatchChanges {
    const stored = appSettingsFromRows(read.viewId, read.rows);
    const config = patch.appBaseConfig
      ? rebaseAppSettings(base, patch.appBaseConfig, stored)
      : stored;
    const next = appSettingsToRows(read.viewId, config, {
      generation: read.state.generation,
      revision: read.state.revision + 1,
    });
    return {
      changes: planAppSettingsChanges(read.rows, next),
      containerCells: Object.fromEntries(
        Object.entries(configRowFieldCells(patch)).filter(
          ([columnId, value]) => !sameCell(read.containerCell(columnId), value)
        )
      ),
    };
  }

  /** The Config tables a change touches; `:revision` alone touches none. */
  private changedTables(
    read: SettingsRead,
    { changes, containerCells }: PatchChanges
  ): string[] {
    return this.tables
      .map(({ tableKey }) => tableKey)
      .filter(tableKey => {
        const change = changes.get(tableKey);
        if (change?.insertRows.length || change?.deleteRowIds.length)
          return true;
        if (tableKey !== APP_SETTINGS_CONTAINER_TABLE)
          return !!change?.updates.length;
        return (
          Object.keys(containerCells).length > 0 ||
          (change?.updates ?? []).some(
            update =>
              update.rowId !== read.viewId ||
              update.columnId !== SETTINGS_REVISION_COLUMN
          )
        );
      });
  }

  /** The changed rows of every table plus the container row, as one group; null for no change. */
  private planPatch(
    read: SettingsRead,
    patch: Partial<ViewConfigRow>,
    base: AppBaseConfig
  ): ReturnType<typeof DatabaseBlockDataSource.planRowSetGroup> | null {
    const planned = this.patchChanges(read, patch, base);
    // Only `:revision` would change: the patch holds the stored settings.
    if (!this.changedTables(read, planned).length) return null;
    const { changes, containerCells } = planned;
    const entries: Array<{
      source: DatabaseBlockDataSource;
      set: DatabaseRowSet;
    }> = [];
    for (const { tableKey, blockId } of this.tables) {
      const change = changes.get(tableKey);
      const values = new Map<string, Record<string, unknown>>();
      for (const update of change?.updates ?? []) {
        let row = values.get(update.rowId);
        if (!row) values.set(update.rowId, (row = {}));
        row[update.columnId] = update.clear ? null : update.value;
      }
      if (tableKey === APP_SETTINGS_CONTAINER_TABLE)
        values.set(read.viewId, {
          ...values.get(read.viewId),
          ...containerCells,
        });
      const upserts = [
        ...(change?.insertRows ?? []).map(row => ({
          rowId: row.id,
          values: { ...appSettingsRowCells(tableKey, row) },
        })),
        ...[...values].map(([rowId, rowValues]) => ({
          rowId,
          values: rowValues,
          // Compare and set: the write holds only on the state it was planned on.
          ...(tableKey === APP_SETTINGS_CONTAINER_TABLE
            ? {
                expected: {
                  [SETTINGS_GENERATION_COLUMN]: read.containerCell(
                    SETTINGS_GENERATION_COLUMN
                  ),
                  [SETTINGS_REVISION_COLUMN]: read.containerCell(
                    SETTINGS_REVISION_COLUMN
                  ),
                },
              }
            : {}),
        })),
      ];
      const deleteRowIds = [...(change?.deleteRowIds ?? [])];
      if (!upserts.length && !deleteRowIds.length) continue;
      entries.push({
        source: this.requireRuntime(blockId).dataSource,
        set: { upserts, deleteRowIds },
      });
    }
    return DatabaseBlockDataSource.planRowSetGroup(entries);
  }

  private requireRead(viewId: string, operation: string): SettingsRead {
    const read = this.readSettings();
    if (!read)
      throw new Error(
        `ViewConfigRowStore.${operation}: Config tables are not loaded`
      );
    if (read.viewId !== viewId)
      throw new Error(
        `ViewConfigRowStore.${operation}: row "${viewId}" missing`
      );
    return read;
  }

  private requireRuntime(blockId: string): TableRuntime {
    const runtime = this.getRuntime(blockId);
    if (!runtime)
      throw new Error(`ViewConfigRowStore: database ${blockId} is not loaded`);
    return runtime;
  }

  private safeReadSnapshot(): ViewConfigRowsSnapshot {
    let read: SettingsRead | null;
    try {
      read = this.readSettings();
    } catch (error) {
      return this.invalidSnapshot(this.containerRowId(), error);
    }
    if (!read) return LOADING_SNAPSHOT;
    try {
      const row = configRowFromCells(
        read.viewId,
        read.containerCell,
        appSettingsFromRows(read.viewId, read.rows)
      );
      return {
        state: 'ready',
        rows: [row],
        invalidRows: [],
        tableRows: read.rows,
      };
    } catch (error) {
      return this.invalidSnapshot(read.viewId, error);
    }
  }

  private invalidSnapshot(
    viewId: string | null,
    error: unknown
  ): ViewConfigRowsSnapshot {
    const invalid =
      error instanceof ViewConfigRowInvalidError
        ? error
        : new ViewConfigRowInvalidError([
            {
              code: 'custom',
              path: ['appBaseConfig'],
              message: error instanceof Error ? error.message : String(error),
            },
          ]);
    console.error('[ViewConfigRowStore] invalid row', viewId, invalid.message);
    return {
      state: 'unavailable',
      rows: [],
      invalidRows: [{ viewId: viewId ?? '', error: invalid }],
      tableRows: NO_TABLE_ROWS,
    };
  }

  private containerRowId(): string | null {
    const read = this.readRuntime(this.location.configDbBlockId);
    return read.state === 'ready'
      ? (this.readTableRowIds(read.runtime)[0] ?? null)
      : null;
  }

  /**
   * The settings of the valid generation; null while a table or one of its
   * row shards is not loaded. Throws with the table and the row when a row
   * cannot be read or the container row is missing.
   */
  private readSettings(): SettingsRead | null {
    const rows: Record<string, SettingsRow[]> = {};
    let container: TableRead | null = null;
    for (const { tableKey, blockId } of this.tables) {
      const runtime = this.getRuntime(blockId);
      if (!runtime) return null;
      const table = this.readTable(tableKey, runtime);
      if (!table) return null;
      rows[tableKey] = table.rows;
      if (tableKey === APP_SETTINGS_CONTAINER_TABLE) container = table;
    }
    const containerRows = rows[APP_SETTINGS_CONTAINER_TABLE] ?? [];
    if (!container || containerRows.length === 0)
      throw new Error(
        `Config table ${APP_SETTINGS_CONTAINER_TABLE}: the container row of ${this.location.configDbBlockId} is missing`
      );
    if (containerRows.length > 1)
      throw new Error(
        `Config table ${APP_SETTINGS_CONTAINER_TABLE} holds ${containerRows.length} container rows`
      );
    const viewId = containerRows[0]!.id;
    const state = appSettingsState(viewId, rows);
    const { current } = splitAppSettingsGenerations(rows, state.generation);
    const containerCell = container.cells.get(viewId)!;
    return { viewId, state, rows: current, containerCell };
  }

  /**
   * The row shards of the table, from every part of its directory; null
   * while a directory part is still loading (a partial list would read as
   * removed rows). Asking requests the missing parts. Throws once the server
   * confirmed it does not hold a part: the table never reads complete.
   */
  private completeShards(
    tableKey: string,
    runtime: TableRuntime
  ): CompleteShards | null {
    const directory = runtime.dataSource.getCompleteShardDirectory();
    if (directory.status === 'loading') return null;
    if (directory.status === 'missing')
      throw new Error(
        `Config table ${tableKey}: the server holds no directory part ${directory.partDocIds.join(', ')}`
      );
    return directory.manifest.shards;
  }

  /** Every row of the table; null while a row shard is not loaded completely. */

  private readTable(tableKey: string, runtime: TableRuntime): TableRead | null {
    const lineage = new Map(
      databaseLineageColumns(runtime.model.props?.columns ?? []).map(
        column => [column.id, column] as const
      )
    );
    const shards = this.completeShards(tableKey, runtime);
    if (!shards) return null;
    const rows: SettingsRow[] = [];
    const cells = new Map<string, (columnId: string) => unknown>();
    for (const shard of shards) {
      const doc = this.ensureWorkspaceDoc(shard.serverDocId)?.spaceDoc;
      if (!doc) return null;
      const rowsMap = doc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      const order: string[] = doc
        .getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
        .toArray()
        .map(String);
      // A partial table would read as removed rows and be written back so.
      if (order.length !== shard.rowCount) return null;
      for (const rowId of order) {
        const row = rowsMap.get(rowId);
        if (!row || typeof row.get !== 'function') return null;
        const cell = cellOfRow(row, lineage);
        cells.set(rowId, cell);
        try {
          rows.push(appSettingsRowFromCells(tableKey, rowId, cell));
        } catch (error) {
          throw new Error(
            `Config table ${tableKey}, row ${rowId}: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
    }
    return { rows, cells };
  }

  /** Row ids of the shards loaded so far; names a row in an error only. */
  private readTableRowIds(runtime: TableRuntime): string[] {
    const directory = runtime.dataSource.getCompleteShardDirectory();
    if (directory.status !== 'ready') return [];
    const rowIds: string[] = [];
    for (const shard of directory.manifest.shards) {
      const doc = this.ensureWorkspaceDoc(shard.serverDocId)?.spaceDoc;
      if (doc)
        rowIds.push(
          ...doc
            .getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
            .toArray()
            .map(String)
        );
    }
    return rowIds;
  }

  /**
   * Observes one table: its root, its storage readiness and its row shards,
   * each as the instance open now; a reopened document is a new one.
   */
  private watchTable(
    blockId: string,
    onChange: () => void
  ): { sync: () => void; dispose: () => void } {
    let rootStore: AppSettingsStore | null = null;
    let rootSub: { unsubscribe: () => void } | undefined;
    let observedModel: unknown = null;
    let readyDispose: (() => void) | undefined;
    let shardDocs: AppSettingsYDoc[] = [];
    const unwatchShards = () => {
      for (const doc of shardDocs) doc.off('update', onChange);
      shardDocs = [];
    };
    return {
      sync: () => {
        const store = this.getDetachedRootStore(blockId);
        if (store !== rootStore) {
          rootSub?.unsubscribe();
          rootStore = store;
          rootSub = store?.slots?.blockUpdated?.subscribe(onChange);
        }
        const model = this.getHostModel(blockId);
        if (model !== observedModel) {
          readyDispose?.();
          readyDispose = undefined;
          observedModel = model;
          if (model)
            readyDispose = observeDatabaseStorageModelReady(model, onChange);
        }
        // An unreadable table has no shards to watch; the snapshot names it.
        const read = this.readRuntime(blockId);
        if (read.state !== 'ready') return;
        // Directory parts and the row shards of the loaded parts; shards of
        // a part that arrives later join on the next change.
        const docIds = readDatabaseShardDirectoryDocIds(read.runtime.model);
        const docs = (
          docIds ? [...docIds.partDocIds, ...docIds.shardDocIds] : []
        ).flatMap(docId => {
          const doc = this.ensureWorkspaceDoc(docId)?.spaceDoc;
          return doc ? [doc] : [];
        });
        if (
          docs.length === shardDocs.length &&
          docs.every((doc, index) => doc === shardDocs[index])
        )
          return;
        unwatchShards();
        shardDocs = docs;
        for (const doc of docs) doc.on('update', onChange);
      },
      dispose: () => {
        rootSub?.unsubscribe();
        rootSub = undefined;
        readyDispose?.();
        unwatchShards();
      },
    };
  }

  private getBlockSuiteDoc(): AppSettingsStore | null {
    const anyDoc = this.docService.doc as any;
    return (anyDoc?.blockSuiteDoc as AppSettingsStore) ?? null;
  }

  private getWorkspace(): AppSettingsWorkspace | null {
    return this.getBlockSuiteDoc()?.workspace ?? null;
  }

  /** A visible host block wins, otherwise the detached canonical root. */
  private getHostModel(blockId: string): any | null {
    const hostBlock = this.getBlockSuiteDoc()?.getBlock(blockId);
    if (hostBlock?.model) return hostBlock.model;
    return this.getDetachedRootStore(blockId)?.getBlock(blockId)?.model ?? null;
  }

  private getDetachedRootStore(blockId: string): AppSettingsStore | null {
    const workspace = this.getWorkspace();
    return workspace
      ? (openDatabaseRootDoc(workspace, blockId)?.store ?? null)
      : null;
  }

  private ensureWorkspaceDoc(docId: string) {
    const workspace = this.getWorkspace();
    return workspace ? openWorkspaceDoc(workspace, docId) : null;
  }

  /** The runtime of a table; null while it loads, throws once it cannot be read. */
  private getRuntime(blockId: string): TableRuntime | null {
    const read = this.readRuntime(blockId);
    if (read.state === 'unreadable') throw read.error;
    return read.state === 'ready' ? read.runtime : null;
  }

  private readRuntime(blockId: string): RuntimeRead {
    const hostModel = this.getHostModel(blockId);
    if (!hostModel) return { state: 'loading' };
    let ready: boolean;
    try {
      ready = isDatabaseStorageModelReady(hostModel);
    } catch (error) {
      return {
        state: 'unreadable',
        error: new Error(
          `Config table ${this.tableKeyOf(blockId)}: the storage of database ${blockId} cannot be read: ${
            error instanceof Error ? error.message : String(error)
          }`,
          { cause: error }
        ),
      };
    }
    if (!ready) return { state: 'loading' };
    const cached = this.runtimes.get(blockId);
    if (cached && cached.hostModel === hostModel)
      return { state: 'ready', runtime: cached };
    cached?.dataSource.dispose();
    const dataSource = createDatabaseBlockDataSource(hostModel);
    const runtime = { dataSource, hostModel, model: dataSource.storageModel };
    this.runtimes.set(blockId, runtime);
    return { state: 'ready', runtime };
  }

  private tableKeyOf(blockId: string): string {
    const table = this.tables.find(entry => entry.blockId === blockId);
    if (!table)
      throw new Error(`ViewConfigRowStore: ${blockId} is no Config table`);
    return table.tableKey;
  }
}
